import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
} from "@mui/material";

import {
    Add,
    Delete,
    Edit,
} from "@mui/icons-material";

import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { imageBaseurl } from "../utils/common";

const initialForm = {
    channel_name: "",
    slug: "",
    short_title: "",
    description: "",
    category: "",
    country: "",
    language: "",
    logo: "",
    banner: "",
    is_live: false,
    is_featured: false,
    status: true,
    servers: [
        {
            server_name: "",
            stream_url: "",
            stream_type: "url",
            quality: "HD",
            is_default: true,
            status: true,
        },
    ],
};

export default function ChannelManagementPage() {
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] =
        useState(false);

    const [deleteId, setDeleteId] =
        useState(null);
    const [open, setOpen] = useState(false);

    const [formData, setFormData] =
        useState(initialForm);

    const [editId, setEditId] = useState(null);

    const [search, setSearch] = useState("");

    // ============================
    // GET CHANNELS
    // ============================

    const handleImageUpload = (
        e,
        fieldName
    ) => {

        const file =
            e.target.files[0];

        if (!file) return;

        setFormData((prev) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setDeleteDialog(true);
    };

    const confirmDelete = async () => {
        try {

            await apiClient.delete(
                `/api/admin/channel/${deleteId}`
            );

            toast.success(
                "Channel deleted successfully"
            );

            getChannels();

            setDeleteDialog(false);

            setDeleteId(null);

        } catch (error) {

            console.log(error);

            toast.error(
                "Delete failed"
            );
        }
    };
    const getChannels = async () => {
        try {
            setLoading(true);

            const res = await apiClient.get(
                "/api/admin/channel"
            );

            setChannels(res.data.data || []);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getChannels();
    }, []);

    // ============================
    // HANDLE INPUT
    // ============================
    const handleChange = (e) => {
        const { name, value, checked, type } =
            e.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : value,
        }));
    };

    // ============================
    // SERVER CHANGE
    // ============================
    const handleServerChange = (
        index,
        field,
        value
    ) => {
        const updatedServers = [
            ...formData.servers,
        ];

        updatedServers[index][field] = value;

        setFormData({
            ...formData,
            servers: updatedServers,
        });
    };

    // ============================
    // ADD SERVER
    // ============================
    const addServer = () => {
        setFormData({
            ...formData,
            servers: [
                ...formData.servers,
                {
                    server_name: "",
                    stream_url: "",
                    stream_type: "url",
                    quality: "HD",
                    is_default: false,
                    status: true,
                },
            ],
        });
    };

    // ============================
    // REMOVE SERVER
    // ============================
    const removeServer = (index) => {
        const updatedServers =
            formData.servers.filter(
                (_, i) => i !== index
            );

        setFormData({
            ...formData,
            servers: updatedServers,
        });
    };

    // ============================
    // SUBMIT
    // ============================
    const handleSubmit = async () => {
        // =========================
        // BASIC VALIDATION
        // =========================

        if (!formData.channel_name?.trim()) {
            return toast.error(
                "Channel name is required"
            );
        }

        if (!formData.short_title?.trim()) {
            return toast.error(
                "Short title is required"
            );
        }

        // if (!formData.slug?.trim()) {
        //     return toast.error(
        //         "Slug is required"
        //     );
        // }

        // if (!formData.category?.trim()) {
        //     return toast.error(
        //         "Category is required"
        //     );
        // }

        // if (!formData.country?.trim()) {
        //     return toast.error(
        //         "Country is required"
        //     );
        // }

        if (!formData.description?.trim()) {
            return toast.error(
                "Description is required"
            );
        }

        // =========================
        // SERVER VALIDATION
        // =========================

        if (
            !formData.servers ||
            formData.servers.length === 0
        ) {
            return toast.error(
                "At least one server is required"
            );
        }

        for (
            let i = 0;
            i < formData.servers.length;
            i++
        ) {
            const server =
                formData.servers[i];

            if (
                !server.server_name?.trim()
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Server name is required`
                );
            }

            if (
                !server.stream_url?.trim()
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Stream URL is required`
                );
            }

            // URL VALIDATION
            const urlPattern =
                /^(https?:\/\/)/i;

            if (
                !urlPattern.test(
                    server.stream_url
                )
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Invalid stream URL`
                );
            }

            if (
                !server.stream_type
            ) {
                return toast.error(
                    `Server ${i + 1
                    }: Stream type is required`
                );
            }
        }

        try {
            const payload =
                new FormData();

            payload.append(
                "channel_name",
                formData.channel_name
            );

            payload.append(
                "short_title",
                formData.short_title
            );

            payload.append(
                "description",
                formData.description
            );

            payload.append(
                "status",
                formData.status
            );

            payload.append(
                "servers",
                JSON.stringify(
                    formData.servers
                )
            );

            // IMAGE
            if (
                formData.logo instanceof File
            ) {

                payload.append(
                    "logo",
                    formData.logo
                );
            }

            // if (
            //     formData.banner instanceof File
            // ) {

            //     payload.append(
            //         "banner",
            //         formData.banner
            //     );
            // }

            if (editId) {

                await apiClient.put(
                    `/api/admin/channel/${editId}`,
                    payload,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

            } else {

                await apiClient.post(
                    "/api/admin/channel",
                    payload,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );
            }
            // if (editId) {
            //     await apiClient.put(
            //         `/api/admin/channel/${editId}`,
            //         formData
            //     );
            // } else {
            //     await apiClient.post(
            //         "/api/admin/channel",
            //         formData
            //     );
            // }

            getChannels();

            setOpen(false);

            setFormData(initialForm);

            setEditId(null);
        } catch (error) {
            toast.error(error?.response?.data?.message)
            console.log(error);
        }
    };

    // ============================
    // EDIT
    // ============================
    const handleEdit = (item) => {
        setFormData({ ...item, servers: JSON.parse(JSON.parse(item?.servers)) });
        setEditId(item.id);
        setOpen(true);
    };

    // ============================
    // FILTER
    // ============================
    const filteredChannels =
        channels.filter((item) =>
            item.channel_name
                ?.toLowerCase()
                .includes(search.toLowerCase())
        );

    return (
        <Box p={3}>
            <Card>
                <CardContent>
                    {/* HEADER */}
                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        justifyContent="space-between"
                        spacing={2}
                        mb={3}
                    >
                        <Typography variant="h5">
                            Live Channel Management
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={2}
                        >
                            <TextField
                                size="small"
                                placeholder="Search Channel"
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                            />

                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => {
                                    setOpen(true);
                                    setEditId(null);
                                    setFormData(initialForm);
                                }}
                            >
                                Add Channel
                            </Button>
                        </Stack>
                    </Stack>

                    {/* TABLE */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        Channel
                                    </TableCell>

                                    {/* <TableCell>
                                        Category
                                    </TableCell> */}

                                    <TableCell>
                                        Servers
                                    </TableCell>

                                    {/* <TableCell>
                                        Live
                                    </TableCell> */}

                                    <TableCell>
                                        Status
                                    </TableCell>

                                    <TableCell>
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredChannels.map(
                                    (item) => (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={2}
                                                    alignItems="center"
                                                >
                                                    <img
                                                        src={imageBaseurl + item.logo}
                                                        alt=""
                                                        width={50}
                                                        height={50}
                                                        style={{
                                                            borderRadius: 10,
                                                            objectFit:
                                                                "cover",
                                                        }}
                                                    />

                                                    <Box>
                                                        <Typography
                                                            fontWeight={700}
                                                        >
                                                            {
                                                                item.channel_name
                                                            }
                                                        </Typography>

                                                        {/* <Typography
                                                            variant="body2"
                                                        >
                                                            {item.country}
                                                        </Typography> */}
                                                    </Box>
                                                </Stack>
                                            </TableCell>

                                            {/* <TableCell>
                                                <Chip
                                                    label={
                                                        item.category
                                                    }
                                                />
                                            </TableCell> */}

                                            <TableCell>
                                                {
                                                    JSON.parse(JSON.parse(item.servers))
                                                        ?.length
                                                }
                                            </TableCell>

                                            {/* <TableCell>
                                                <Chip
                                                    color={
                                                        item.is_live
                                                            ? "success"
                                                            : "default"
                                                    }
                                                    label={
                                                        item.is_live
                                                            ? "Live"
                                                            : "Offline"
                                                    }
                                                />
                                            </TableCell> */}

                                            <TableCell>
                                                <Chip
                                                    color={
                                                        item.status
                                                            ? "success"
                                                            : "error"
                                                    }
                                                    label={
                                                        item.status
                                                            ? "Active"
                                                            : "Inactive"
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            handleEdit(
                                                                item
                                                            )
                                                        }
                                                    >
                                                        <Edit />
                                                    </IconButton>

                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            handleDeleteClick(
                                                                item.id
                                                            )
                                                        }
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* DIALOG */}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    {editId
                        ? "Edit Channel"
                        : "Add Channel"}
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} mt={1}>

                        <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Channel Name"
                                name="channel_name"
                                value={
                                    formData.channel_name
                                }
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Short Title"
                                name="short_title"
                                value={formData.short_title}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>

                            <Typography mb={1}>
                                Channel Logo
                            </Typography>

                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                            >
                                Upload Logo

                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        handleImageUpload(
                                            e,
                                            "logo"
                                        )
                                    }
                                />
                            </Button>

                            {/* {formData.logo && (
                                <Box mt={2}>
                                    <img
                                        src={formData.logo}
                                        alt="logo"
                                        style={{
                                            width: 100,
                                            height: 100,
                                            borderRadius: 10,
                                            objectFit: "cover",
                                            border:
                                                "1px solid #ddd",
                                        }}
                                    />
                                </Box>
                            )} */}
                            <img
                                style={{ marginTop: '10px' }}
                                src={
                                    formData.logo instanceof File
                                        ? URL.createObjectURL(
                                            formData.logo
                                        )
                                        : imageBaseurl + formData.logo
                                }
                                alt=""
                                width={100}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 12 }}>
                            <Stack
                                direction="row"
                                spacing={4}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                >
                                    <Typography>
                                        Status
                                    </Typography>

                                    <Switch
                                        checked={
                                            formData.status
                                        }
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                status:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                </Stack>
                            </Stack>
                        </Grid>
                        {/* <Grid item size={{ xs: 12, md: 6 }}>

                            <Typography mb={1}>
                                Banner Image
                            </Typography>

                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                            >
                                Upload Banner

                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        handleImageUpload(
                                            e,
                                            "banner"
                                        )
                                    }
                                />
                            </Button>

                            {formData.banner && (
                                <Box mt={2}>
                                    <img
                                        src={formData.banner}
                                        alt="banner"
                                        style={{
                                            width: "100%",
                                            maxHeight: 150,
                                            borderRadius: 10,
                                            objectFit: "cover",
                                            border:
                                                "1px solid #ddd",
                                        }}
                                    />
                                </Box>
                            )}
                        </Grid> */}
                        {/* <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Slug"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Category"
                                name="category"
                                value={
                                    formData.category
                                }
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Country"
                                name="country"
                                value={
                                    formData.country
                                }
                                onChange={handleChange}
                            />
                        </Grid> */}

                        <Grid item size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                name="description"
                                value={
                                    formData.description
                                }
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* SERVERS */}

                        <Grid item size={{ xs: 12 }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                mb={2}
                            >
                                <Typography variant="h6">
                                    Servers
                                </Typography>

                                <Button
                                    variant="outlined"
                                    onClick={addServer}
                                >
                                    Add Server
                                </Button>
                            </Stack>

                            {formData.servers.map(
                                (server, index) => (
                                    <Card
                                        key={index}
                                        sx={{ mb: 2 }}
                                    >
                                        <CardContent>
                                            <Grid
                                                container
                                                spacing={2}
                                            >
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={3}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        label="Server Name"
                                                        value={
                                                            server.server_name
                                                        }
                                                        onChange={(e) =>
                                                            handleServerChange(
                                                                index,
                                                                "server_name",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>

                                                <Grid
                                                    item
                                                    size={{ xs: 12, md: 4 }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        label="Stream URL"
                                                        value={
                                                            server.stream_url
                                                        }
                                                        onChange={(e) =>
                                                            handleServerChange(
                                                                index,
                                                                "stream_url",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    size={{ xs: 12, md: 2 }}
                                                >
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        label="Type"
                                                        value={
                                                            server.stream_type
                                                        }
                                                        onChange={(e) =>
                                                            handleServerChange(
                                                                index,
                                                                "stream_type",
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <MenuItem value="url">
                                                            Url
                                                        </MenuItem>

                                                        <MenuItem value="iframe">
                                                            Iframe
                                                        </MenuItem>

                                                        {/* <MenuItem value="youtube">
                                                            Youtube
                                                        </MenuItem> */}
                                                    </TextField>
                                                </Grid>
                                                {/* 

                                                <Grid
                                                    item
                                                    size={{ xs: 12, md: 2 }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        label="Quality"
                                                        value={
                                                            server.quality
                                                        }
                                                        onChange={(e) =>
                                                            handleServerChange(
                                                                index,
                                                                "quality",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid> */}

                                                <Grid
                                                    item
                                                    size={{ xs: 12, md: 1 }}
                                                >
                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            removeServer(
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                )
                            )}
                        </Grid>

                        <Grid item size={{ xs: 12, }}>
                            <Stack
                                direction="row"
                                spacing={4}
                            >
                                {/* <Stack
                                    direction="row"
                                    alignItems="center"
                                >
                                    <Typography>
                                        Status
                                    </Typography>

                                    <Switch
                                        checked={
                                            formData.status
                                        }
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                status:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                </Stack> */}
                                {/* <Stack
                                    direction="row"
                                    alignItems="center"
                                >
                                    <Typography>
                                        Live
                                    </Typography>

                                    <Switch
                                        checked={
                                            formData.is_live
                                        }
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                is_live:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                </Stack> */}

                                {/* <Stack
                                    direction="row"
                                    alignItems="center"
                                >
                                    <Typography>
                                        Featured
                                    </Typography>

                                    <Switch
                                        checked={
                                            formData.is_featured
                                        }
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                is_featured:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                </Stack> */}
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() =>
                            setOpen(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        {editId
                            ? "Update"
                            : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteDialog}
                onClose={() =>
                    setDeleteDialog(false)
                }
            >

                <DialogTitle>
                    Delete Channel
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Are you sure you want
                        to delete this channel?
                    </Typography>
                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() =>
                            setDeleteDialog(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDelete}
                    >
                        Delete
                    </Button>

                </DialogActions>

            </Dialog>
        </Box>
    );
}